import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "../../../components/feature/Navbar";
import Footer from "../../../components/feature/Footer";
import { supabase } from "../../../lib/supabase";
import { useApp } from "../../../contexts/AppContext";

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;

interface Product {
  id: string;
  title: string;
  description: string;
  price: number;
  thumbnail_url: string | null;
  category: string;
  status: string;
  sales_count: number;
  type: string;
  provider_id: string;
  provider?: {
    id: string;
    name: string;
    service_name: string | null;
    avatar_url: string | null;
  };
}

interface Purchase {
  id: string;
  product_id: string;
}

export default function KitDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { session, isLoggedIn } = useApp();
  const [visible, setVisible] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [alreadyPurchased, setAlreadyPurchased] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    setTimeout(() => setVisible(true), 80);
    if (!id) return;
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("products")
        .select("*, provider:provider_id(id, name, service_name, avatar_url)")
        .eq("id", id)
        .eq("type", "kit")
        .maybeSingle();
      if (data) setProduct(data as Product);
      setLoading(false);
    };
    load();
  }, [id]);

  useEffect(() => {
    if (!session || !id) return;
    const checkPurchase = async () => {
      const { data } = await supabase
        .from("purchases")
        .select("id, product_id")
        .eq("buyer_id", session.user.id)
        .eq("product_id", id)
        .eq("status", "completed")
        .maybeSingle();
      setAlreadyPurchased(!!(data as Purchase | null));
    };
    checkPurchase();
  }, [session, id]);

  const handlePurchase = async () => {
    if (!isLoggedIn || !session) { navigate("/login"); return; }
    if (!product) return;
    setCheckoutLoading(true);
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const res = await fetch(`${SUPABASE_URL}/functions/v1/stripe-checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authSession?.access_token}`,
        },
        body: JSON.stringify({
          productId: product.id,
          productTitle: product.title,
          price: product.price * qty,
          productType: "kit",
          providerId: product.provider_id,
          successUrl: `${window.location.origin}/mypage?purchase=success`,
          cancelUrl: window.location.href,
        }),
      });
      const json = await res.json();
      if (json.error) throw new Error(json.error);
      if (json.url) window.location.href = json.url;
    } catch (err) {
      alert(`エラーが発生しました: ${(err as Error).message}`);
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <Navbar />
        <i className="ri-loader-4-line animate-spin text-3xl text-teal" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-ivory">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-32">
          <i className="ri-box-3-line text-5xl text-charcoal/20 mb-4" />
          <p className="font-serif text-deep-green text-xl mb-2">教材キットが見つかりません</p>
          <p className="text-sm text-charcoal/50 font-sans mb-6">この商品は削除されたか存在しない可能性があります。</p>
          <Link to="/materials" className="bg-deep-green text-white px-6 py-3 rounded-full text-sm font-sans cursor-pointer hover:bg-teal transition-colors">
            教材一覧へ戻る
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const providerName = product.provider?.service_name || product.provider?.name || "販売者";

  return (
    <div className={`min-h-screen bg-ivory transition-opacity duration-700 ${visible ? "opacity-100" : "opacity-0"}`}>
      <Navbar />
      <main className="pt-16">
        <div className="max-w-screen-xl mx-auto px-4 md:px-8 py-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-xs text-charcoal/40 font-sans mb-8 flex-wrap">
            <Link to="/" className="hover:text-teal transition-colors cursor-pointer">ホーム</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/providers" className="hover:text-teal transition-colors cursor-pointer">事業者一覧</Link>
            {product.provider_id && (
              <>
                <i className="ri-arrow-right-s-line" />
                <Link to={`/providers/${product.provider_id}`} className="hover:text-teal transition-colors cursor-pointer">{providerName}</Link>
              </>
            )}
            <i className="ri-arrow-right-s-line" />
            <span className="text-charcoal/70">{product.title}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Image */}
            <div>
              <div className="relative h-80 md:h-96 rounded-2xl overflow-hidden bg-stone-100">
                {product.thumbnail_url ? (
                  <img src={product.thumbnail_url} alt={product.title} className="w-full h-full object-cover object-top" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <i className="ri-box-3-line text-6xl text-charcoal/20" />
                  </div>
                )}
                <div className="absolute top-3 left-3">
                  <span className="bg-gold text-white text-xs px-3 py-1 rounded-full font-sans">教材キット</span>
                </div>
              </div>
            </div>

            {/* Info */}
            <div>
              {product.category && (
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs bg-teal/10 text-teal px-2.5 py-0.5 rounded-full font-sans">{product.category}</span>
                </div>
              )}
              <h1 className="font-serif text-deep-green text-2xl md:text-3xl font-light leading-snug mb-3">{product.title}</h1>
              {product.description && (
                <p className="text-charcoal/65 text-sm font-sans leading-relaxed mb-6">{product.description}</p>
              )}

              <div className="border-t border-stone-100 pt-6 mb-6">
                <p className="font-sans text-3xl text-deep-green font-light mb-1">
                  ¥{product.price.toLocaleString()}<span className="text-xs text-charcoal/40 ml-1">（税込）</span>
                </p>
                <p className="text-xs text-charcoal/50 font-sans">{product.sales_count > 0 ? `${product.sales_count}件の購入実績` : "新着商品"}</p>
              </div>

              {/* Qty */}
              <div className="flex items-center gap-4 mb-5">
                <span className="text-sm text-charcoal/60 font-sans">数量</span>
                <div className="flex items-center border border-stone-200 rounded-full overflow-hidden">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-9 h-9 flex items-center justify-center text-charcoal/60 hover:bg-ivory cursor-pointer">
                    <i className="ri-subtract-line" />
                  </button>
                  <span className="w-8 text-center text-sm font-sans font-medium text-charcoal">{qty}</span>
                  <button onClick={() => setQty(qty + 1)} className="w-9 h-9 flex items-center justify-center text-charcoal/60 hover:bg-ivory cursor-pointer">
                    <i className="ri-add-line" />
                  </button>
                </div>
              </div>

              {alreadyPurchased ? (
                <div className="p-4 bg-teal/10 rounded-xl border border-teal/20">
                  <p className="text-sm text-teal font-sans flex items-center gap-2 mb-2">
                    <i className="ri-checkbox-circle-fill" />購入済みです。マイページで確認できます。
                  </p>
                  <Link to="/mypage" className="block w-full text-center py-2.5 rounded-full border border-teal text-teal text-sm font-sans hover:bg-teal hover:text-white transition-colors cursor-pointer">
                    マイページで確認する
                  </Link>
                </div>
              ) : (
                <button
                  onClick={handlePurchase}
                  disabled={checkoutLoading}
                  className="w-full py-4 rounded-full text-sm font-sans font-medium cursor-pointer transition-all duration-300 whitespace-nowrap btn-primary disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {checkoutLoading ? (
                    <><i className="ri-loader-4-line animate-spin" />処理中...</>
                  ) : `カートに入れて購入する → ¥${(product.price * qty).toLocaleString()}`}
                </button>
              )}
              <div className="mt-5 flex flex-col gap-1.5 text-xs text-charcoal/50 font-sans">
                <span className="flex items-center gap-1.5"><i className="ri-truck-line text-teal" />購入後に配送情報をご案内します</span>
                <span className="flex items-center gap-1.5"><i className="ri-lock-line text-teal" />Stripe決済（安全・クレジットカード対応）</span>
              </div>
            </div>
          </div>

          {/* Provider */}
          {product.provider_id && (
            <div className="mt-10">
              <Link to={`/providers/${product.provider_id}`} className="inline-flex items-center gap-3 bg-white rounded-2xl p-4 border border-stone-100 hover:border-teal transition-colors cursor-pointer group">
                <div className="w-12 h-12 rounded-xl overflow-hidden bg-stone-100 flex-shrink-0">
                  {product.provider?.avatar_url ? (
                    <img src={product.provider.avatar_url} alt={providerName} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><i className="ri-user-line text-charcoal/30 text-xl" /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-charcoal/40 font-sans">販売者</p>
                  <p className="font-serif text-deep-green text-sm font-medium group-hover:text-teal transition-colors">{providerName}</p>
                  <p className="text-xs text-charcoal/50 font-sans">{product.provider?.name}</p>
                </div>
                <i className="ri-arrow-right-line text-charcoal/30 group-hover:text-teal transition-colors" />
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
